import React, { useState } from 'react';
import { 
  Plus, Users, Search, ChevronDown, ChevronUp, Filter,
  Mail, Phone, DollarSign, Tag, Calendar, Edit, Trash2, MessageCircle,
  FileSpreadsheet, FileText
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { NoahTasks } from '../components/NoahTasks';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CustomerProfile } from '../components/CustomerProfile';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { exportCustomers } from '../utils/export';
import { EmptyState } from '../components/EmptyState';

const statusConfig = {
  new: { label: 'Nuevo', color: 'bg-[#2d5b45]', textColor: 'text-brand-deep dark:text-brand-green' },
  contacted: { label: 'Contactado', color: 'bg-[#85562e]', textColor: 'text-amber-700 dark:text-amber-400' },
  qualified: { label: 'Calificado', color: 'bg-emerald-700', textColor: 'text-emerald-700 dark:text-emerald-400' },
  negotiation: { label: 'Negociación', color: 'bg-[#a6703b]', textColor: 'text-amber-700 dark:text-amber-400' },
  won: { label: 'Ganado', color: 'bg-[#0f7a52]', textColor: 'text-emerald-700 dark:text-emerald-400' },
  lost: { label: 'Perdido', color: 'bg-red-700', textColor: 'text-red-600 dark:text-red-400' },
  inquiry: { label: 'Consulta', color: 'bg-[#002d2c]', textColor: 'text-brand-deep dark:text-brand-green' }
};

const Customers = () => {
  const navigate = useNavigate();
  const { customers, campaigns, createCustomer, updateCustomer, deleteCustomer, setSelectedChat, chats } = useApp();
  const { openWorkspace } = useWorkspace();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [campaignFilter, setCampaignFilter] = useState('all');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [profileCustomerId, setProfileCustomerId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', status: 'new', campaign_id: '',
    source: '', value: '', notes: ''
  });

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = !searchTerm || 
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesCampaign = campaignFilter === 'all' || c.campaign_id === campaignFilter;
    return matchesSearch && matchesStatus && matchesCampaign;
  });

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', status: 'new', campaign_id: '', source: '', value: '', notes: '' });
    setEditingCustomer(null);
  };

  const handleEdit = (customer) => {
    setFormData({
      name: customer.name || '',
      email: customer.email || '',
      phone: customer.phone || '',
      status: customer.status || 'new',
      campaign_id: customer.campaign_id || '',
      source: customer.source || '',
      value: customer.value || '',
      notes: customer.notes || ''
    });
    setEditingCustomer(customer);
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) return;
    const data = { ...formData, value: formData.value ? parseFloat(formData.value) : 0 };
    if (editingCustomer) {
      await updateCustomer(editingCustomer.id, data);
    } else {
      await createCustomer(data);
    }
    resetForm();
    setShowForm(false);
  };

  const handleOpenChat = (customer) => {
    const chat = chats.find(c => c.customer?.id === customer.id || c.customer_id === customer.id);
    if (chat) {
      setSelectedChat(chat);
      navigate('/inbox');
    }
  };

  return (
    <>
    <div data-testid="customers-page" className="min-h-screen">
      <Header title="Clientes" subtitle={`${filteredCustomers.length} clientes`}>
        <Button
          data-testid="export-customers-excel"
          variant="outline"
          size="sm"
          className="gap-1"
          onClick={() => { exportCustomers(filteredCustomers, 'excel'); toast.success('Excel exportado'); }}
        >
          <FileSpreadsheet className="w-4 h-4" /> Excel
        </Button>
        <Button
          data-testid="export-customers-pdf"
          variant="outline"
          size="sm"
          className="gap-1"
          onClick={() => { exportCustomers(filteredCustomers, 'pdf'); toast.success('PDF exportado'); }}
        >
          <FileText className="w-4 h-4" /> PDF
        </Button>
        <Button
          data-testid="new-customer-btn"
          onClick={() => { resetForm(); setShowForm(true); }}
          className="gap-2 bg-primary hover:opacity-90"
        >
          <Plus className="w-4 h-4" />
          Nuevo Cliente
        </Button>
      </Header>

      <div className="p-6 space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              data-testid="customer-search"
              placeholder="Buscar cliente..."
              className="pl-9 bg-muted border-none"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="status-filter">
              <SelectValue placeholder="Estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {Object.entries(statusConfig).map(([key, cfg]) => (
                <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={campaignFilter} onValueChange={setCampaignFilter}>
            <SelectTrigger className="w-44" data-testid="campaign-filter">
              <SelectValue placeholder="Campaña" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las campañas</SelectItem>
              {campaigns.map(c => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Customer List */}
        <div className="space-y-2">
          {filteredCustomers.map((customer) => {
            const isExpanded = expandedCustomer === customer.id;
            const campaign = campaigns.find(c => c.id === customer.campaign_id);
            const cfg = statusConfig[customer.status] || statusConfig.new;

            return (
              <Card
                key={customer.id}
                data-testid={`customer-card-${customer.id}`}
                className="glass-card overflow-hidden transition-all duration-300"
              >
                <CardContent className="p-0">
                  {/* Collapsed header */}
                  <div
                    className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => setExpandedCustomer(isExpanded ? null : customer.id)}
                  >
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarFallback className={cn("text-white text-sm font-semibold ring-1 ring-black/10", cfg.color)}>
                          {customer.name?.charAt(0) || '?'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-x-2 gap-y-1 flex-wrap">
                          <span 
                            className="font-medium cursor-pointer truncate max-w-full hover:text-brand-deep dark:text-brand-deep dark:text-brand-green transition-colors"
                            onClick={(e) => { e.stopPropagation(); setProfileCustomerId(customer.id); }}
                          >{customer.name}</span>
                          <Badge variant="outline" className={cn("text-xs", cfg.textColor)}>
                            {cfg.label}
                          </Badge>
                          {campaign && (
                            <Badge variant="outline" className="text-xs text-foreground max-w-[150px] truncate" style={{ borderColor: campaign.color, backgroundColor: `${campaign.color}22` }}>
                              {campaign.name}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{customer.email || customer.phone || 'Sin contacto'}</p>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-4 shrink-0">
                        {customer.value > 0 && (
                          <span className="text-sm font-medium">${customer.value.toLocaleString()}</span>
                        )}
                        <span className="hidden sm:inline text-xs text-muted-foreground capitalize">{customer.source || '-'}</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </div>
                  </div>

                  {/* Expanded details */}
                  {isExpanded && (
                    <div className="border-t border-border p-4 space-y-4 animate-expand-in">
                      <NoahTasks customerId={customer.id} campaignId={customer.campaign_id} />
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Mail className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Email</span>
                          </div>
                          <p className="text-sm font-medium">{customer.email || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Phone className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Teléfono</span>
                          </div>
                          <p className="text-sm font-medium">{customer.phone || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <DollarSign className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Valor</span>
                          </div>
                          <p className="text-sm font-medium">${customer.value?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Calendar className="w-4 h-4 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">Fuente</span>
                          </div>
                          <p className="text-sm font-medium capitalize">{customer.source || 'Direct'}</p>
                        </div>
                      </div>

                      {customer.notes && (
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <span className="text-xs text-muted-foreground">Notas</span>
                          <p className="text-sm mt-1">{customer.notes}</p>
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => handleOpenChat(customer)}>
                          <MessageCircle className="w-4 h-4 mr-1" /> Chat
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEdit(customer)}>
                          <Edit className="w-4 h-4 mr-1" /> Editar
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => deleteCustomer(customer.id)}>
                          <Trash2 className="w-4 h-4 mr-1" /> Eliminar
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {filteredCustomers.length === 0 && (
          <Card className="glass-card border-dashed">
            <CardContent className="py-12 text-center">
              <EmptyState
                mood="search"
                title="Sin clientes"
                description="No se encontraron clientes con los filtros actuales. Prueba a limpiar la búsqueda o crea tu primer cliente."
                testId="customers-empty"
              />
            </CardContent>
          </Card>
        )}
      </div>

      {/* Customer Form Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) resetForm(); setShowForm(open); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Nombre completo" data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="email@ejemplo.com" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="+1234567890" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Estado</Label>
                <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusConfig).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Campaña</Label>
                <Select value={formData.campaign_id || "none"} onValueChange={v => setFormData({...formData, campaign_id: v === "none" ? "" : v})}>
                  <SelectTrigger><SelectValue placeholder="Sin campaña" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin campaña</SelectItem>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fuente</Label>
                <Select value={formData.source} onValueChange={v => setFormData({...formData, source: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="facebook">Facebook</SelectItem>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="web">Web</SelectItem>
                    <SelectItem value="referral">Referido</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor ($)</Label>
                <Input type="number" value={formData.value} onChange={(e) => setFormData({...formData, value: e.target.value})} placeholder="0.00" />
              </div>
            </div>
            <Button
              data-testid="save-customer-btn"
              className="w-full bg-primary"
              onClick={handleSubmit}
              disabled={!formData.name.trim()}
            >
              {editingCustomer ? 'Guardar Cambios' : 'Crear Cliente'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>

      {profileCustomerId && (
        <CustomerProfile
          customerId={profileCustomerId}
          onClose={() => setProfileCustomerId(null)}
          onNavigate={(page) => { setProfileCustomerId(null); openWorkspace(page); }}
        />
      )}
    </>
  );
};

export default Customers;
